import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";

import useToast from "../../components/useToast";
import { AuthContext } from "../../AuthContext";
import { API_BASE_URL } from "../../config";
import AppButton from "../../design-system/components/AppButton";
import AppCallout from "../../design-system/components/AppCallout";
import AppConfirmDialog from "../../design-system/components/AppConfirmDialog";
import AppLoadingOverlay from "../../design-system/components/AppLoadingOverlay";
import AppPeriodNavigator from "../../design-system/components/AppPeriodNavigator";
import AppToolbar from "../../design-system/components/AppToolbar";
import AppTooltip from "../../design-system/components/AppTooltip";
import AppWorkspace from "../../design-system/components/AppWorkspace";
import { IconDelete, IconRecurringCalendar } from "../../design-system/icons";
import DefaultDemandDialog from "./DefaultDemandDialog";
import DemandDepartmentCard from "./DemandDepartmentCard";
import {
  defaultKey,
  demandKey,
  formatWeekRange,
  hasAnyDemand,
  parseHeadcount,
} from "./demandModel";
import "../../design-system/styles/tokens.css";
import "../../design-system/styles/components.css";
import "./demand.css";

const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const toISODate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const mondayFrom = (value) => {
  const source = value ? new Date(`${value}T12:00:00`) : new Date();
  const day = source.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  source.setDate(source.getDate() + diff);
  return toISODate(source);
};

// Eine Woche vor oder zurück, immer auf den Montag normiert.
const stepWeek = (weekStart, direction) =>
  mondayFrom(toISODate(addDays(new Date(`${weekStart}T12:00:00`), direction * 7)));

const EMPTY_ARRAY = [];
const AUTO_SAVE_DELAY_MS = 800;
// Am Zahlenfeld wird geklickt und gehalten — jede gespeicherte Zwischenstufe
// duerfte sonst eine eigene Meldung ausloesen. Die Bestaetigung wartet
// deshalb, bis eine Weile nichts mehr gespeichert wurde, und kommt dann
// einmal fuer die ganze Serie.
const SAVE_TOAST_DELAY_MS = 1000;

const Demand = () => {
  const { t } = useTranslation();
  const { error: showError, success: showSuccess } = useToast();
  const { selectedLocationId } = useContext(AuthContext);
  const token = localStorage.getItem("session_token");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [values, setValues] = useState({});
  const [overrides, setOverrides] = useState({});
  const [defaultValues, setDefaultValues] = useState({});
  const [weekStart, setWeekStart] = useState(() => mondayFrom());
  const [defaultDialogOpen, setDefaultDialogOpen] = useState(false);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [defaultDirty, setDefaultDirty] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [expandedDepartmentIds, setExpandedDepartmentIds] = useState(() => new Set());
  const [defaultExpandedDepartmentIds, setDefaultExpandedDepartmentIds] = useState(() => new Set());
  const valuesRef = useRef(values);
  const defaultValuesRef = useRef(defaultValues);
  const editVersionRef = useRef(0);
  const defaultEditVersionRef = useRef(0);
  const saveToastTimerRef = useRef(null);

  const dates = data?.dates || EMPTY_ARRAY;
  const departments = data?.departments || EMPTY_ARRAY;

  useEffect(() => {
    valuesRef.current = values;
  }, [values]);

  useEffect(() => {
    defaultValuesRef.current = defaultValues;
  }, [defaultValues]);

  const shiftCount = useMemo(
    () => departments.reduce((sum, department) => sum + (department.shifts || []).length, 0),
    [departments]
  );

  // Jeder erfolgreiche Speichervorgang schiebt die Meldung nach hinten. Wer
  // eine Zahl hochdreht, sieht am Ende genau eine Bestaetigung.
  const announceSaved = useCallback(() => {
    if (saveToastTimerRef.current) {
      window.clearTimeout(saveToastTimerRef.current);
    }
    saveToastTimerRef.current = window.setTimeout(() => {
      saveToastTimerRef.current = null;
      showSuccess(t("demand_ui.saved"));
    }, SAVE_TOAST_DELAY_MS);
  }, [showSuccess, t]);

  useEffect(() => () => {
    if (saveToastTimerRef.current) {
      window.clearTimeout(saveToastTimerRef.current);
    }
  }, []);

  const fetchDemand = async ({ showLoading = true } = {}) => {
    if (showLoading) setLoading(true);
    setLoadError("");
    try {
      const response = await axios.get(`${API_BASE_URL}/api/web/demand`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          week_start: weekStart,
          ...(selectedLocationId ? { location_id: selectedLocationId } : {}),
        },
      });
      const nextData = response.data || {};
      setData(nextData);
      setValues(nextData.demands || {});
      setOverrides(nextData.overrides || {});
      setDefaultValues(nextData.default_demands || {});
      setDirty(false);
      setDefaultDirty(false);
    } catch (error) {
      setLoadError(error?.response?.data?.message || t("demand_ui.errors.load"));
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  useEffect(() => {
    fetchDemand();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart, selectedLocationId, token]);

  const saveDemand = useCallback(async (snapshotValues, snapshotVersion) => {
    const entries = [];
    departments.forEach((department) => {
      (department.shifts || []).forEach((shift) => {
        dates.forEach((day) => {
          if (day.is_open === false) return;
          entries.push({
            department_id: department.id,
            shift_id: shift.id,
            date: day.date,
            required_workers: Number(snapshotValues[demandKey(department.id, shift.id, day.date)] || 0),
          });
        });
      });
    });

    setSaving(true);
    try {
      await axios.put(
        `${API_BASE_URL}/api/web/demand`,
        {
          week_start: weekStart,
          location_id: selectedLocationId || data?.selected_location_id,
          entries,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (editVersionRef.current === snapshotVersion) {
        setDirty(false);
      }
      announceSaved();
    } catch (error) {
      const message = error?.response?.data?.message || t("demand_ui.errors.auto_save");
      showError(message);
    } finally {
      setSaving(false);
    }
  }, [announceSaved, data?.selected_location_id, dates, departments, selectedLocationId, showError, t, token, weekStart]);

  const saveDefaultDemand = useCallback(async (snapshotValues, snapshotVersion) => {
    const entries = [];
    departments.forEach((department) => {
      (department.shifts || []).forEach((shift) => {
        dates.forEach((day) => {
          if (day.is_open === false) return;
          entries.push({
            department_id: department.id,
            shift_id: shift.id,
            weekday: day.weekday_index,
            required_workers: Number(snapshotValues[defaultKey(department.id, shift.id, day.weekday_index)] || 0),
          });
        });
      });
    });

    setSaving(true);
    try {
      await axios.put(
        `${API_BASE_URL}/api/web/demand/default`,
        {
          location_id: selectedLocationId || data?.selected_location_id,
          entries,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (defaultEditVersionRef.current === snapshotVersion) {
        setDefaultDirty(false);
      }
      announceSaved();
    } catch (error) {
      const message = error?.response?.data?.message || t("demand_ui.errors.default_auto_save");
      showError(message);
    } finally {
      setSaving(false);
    }
  }, [announceSaved, data?.selected_location_id, dates, departments, selectedLocationId, showError, t, token]);

  useEffect(() => {
    if (!dirty || loading || saving || !data) return undefined;

    const snapshotVersion = editVersionRef.current;
    const timer = window.setTimeout(() => {
      saveDemand(valuesRef.current, snapshotVersion);
    }, AUTO_SAVE_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [data, dirty, loading, saveDemand, saving, values]);

  useEffect(() => {
    if (!defaultDirty || loading || saving || !data) return undefined;

    const snapshotVersion = defaultEditVersionRef.current;
    const timer = window.setTimeout(() => {
      saveDefaultDemand(defaultValuesRef.current, snapshotVersion);
    }, AUTO_SAVE_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [data, defaultDirty, defaultValues, loading, saveDefaultDemand, saving]);

  const updateCell = (departmentId, shiftId, day, value) => {
    if (day?.is_open === false) return;
    const parsed = parseHeadcount(value);
    const key = demandKey(departmentId, shiftId, day.date);
    const defaultValue = Number(defaultValues[defaultKey(departmentId, shiftId, day.weekday_index)] || 0);
    editVersionRef.current += 1;
    setValues((current) => ({
      ...current,
      [key]: parsed,
    }));
    setOverrides((current) => {
      const next = { ...current };
      const numericValue = Number(parsed || 0);
      if (numericValue === defaultValue) {
        delete next[key];
      } else {
        next[key] = numericValue;
      }
      return next;
    });
    setDirty(true);
  };

  const updateDefaultCell = (departmentId, shiftId, day, value) => {
    if (day?.is_open === false) return;
    const parsed = parseHeadcount(value);
    const numericValue = Number(parsed || 0);
    const key = defaultKey(departmentId, shiftId, day.weekday_index);

    defaultEditVersionRef.current += 1;
    setDefaultValues((current) => ({
      ...current,
      [key]: parsed,
    }));
    setValues((current) => {
      const next = { ...current };
      dates.forEach((dateItem) => {
        if (dateItem.weekday_index !== day.weekday_index || dateItem.is_open === false) return;
        const weekKey = demandKey(departmentId, shiftId, dateItem.date);
        if (!Object.prototype.hasOwnProperty.call(overrides, weekKey)) {
          next[weekKey] = numericValue;
        }
      });
      return next;
    });
    setDefaultDirty(true);
  };

  // Betriebsferien & Co.: die ganze Woche auf 0, der Standardbedarf bleibt.
  const clearWeek = async () => {
    setClearing(true);
    // Einen noch wartenden Auto-Save verwerfen, sonst schreibt er die eben
    // geleerte Woche wieder voll.
    editVersionRef.current += 1;
    setDirty(false);
    try {
      await axios.post(
        `${API_BASE_URL}/api/web/demand/clear-week`,
        {
          week_start: weekStart,
          location_id: selectedLocationId || data?.selected_location_id,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setClearDialogOpen(false);
      await fetchDemand({ showLoading: false });
      showSuccess(t("demand_ui.week_cleared"));
    } catch (error) {
      showError(error?.response?.data?.message || t("demand_ui.errors.clear_week"));
    } finally {
      setClearing(false);
    }
  };

  const toggleDepartment = (departmentId) => {
    setExpandedDepartmentIds((current) => {
      const next = new Set(current);
      if (next.has(departmentId)) next.delete(departmentId);
      else next.add(departmentId);
      return next;
    });
  };

  const toggleDefaultDepartment = (departmentId) => {
    setDefaultExpandedDepartmentIds((current) => {
      const next = new Set(current);
      if (next.has(departmentId)) next.delete(departmentId);
      else next.add(departmentId);
      return next;
    });
  };

  return (
    <AppWorkspace title={t("demand_ui.title")} className="demand-v2" loading={loading}>

      <AppToolbar
        className="demand-v2__toolbar"
        left={(
          <>
            {/* Dieselbe Navigation wie auf /shiftplan und /availability. */}
            <AppPeriodNavigator
              viewMode="week"
              referenceDate={new Date(`${weekStart}T12:00:00`)}
              previousLabel={t("demand_ui.previous_week")}
              nextLabel={t("demand_ui.next_week")}
              onPreviousPeriod={() => setWeekStart(stepWeek(weekStart, -1))}
              onNextPeriod={() => setWeekStart(stepWeek(weekStart, 1))}
              onSelectDate={(date) => setWeekStart(mondayFrom(toISODate(date)))}
            />
          </>
        )}
        right={(
          <>
            {/* Beide Aktionen tragen ihre Beschriftung: der Standardbedarf ist die
                Grundlage der ganzen Seite, und "leeren" muss man ohne Raten
                verstehen, bevor man klickt. */}
            <AppTooltip content={t("demand_ui.clear_week_hint")}>
              <AppButton
                variant="neutral"
                size="md"
                className="demand-v2__toolbar-action"
                disabled={loading || saving || clearing || !hasAnyDemand(values)}
                onClick={() => setClearDialogOpen(true)}
              >
                <IconDelete />
                <span>{t("demand_ui.clear_week_action")}</span>
              </AppButton>
            </AppTooltip>
            <AppTooltip content={t("demand_ui.default_help")}>
              <AppButton
                variant="primary"
                size="md"
                className="demand-v2__toolbar-action"
                onClick={() => {
                  /* Eingeklappt starten: bei mehreren Abteilungen ist der
                     Dialog sonst sofort eine Wand aus Zahlenfeldern. Die
                     Auswahl der vorigen Sitzung wird bewusst nicht behalten. */
                  setDefaultExpandedDepartmentIds(new Set());
                  setDefaultDialogOpen(true);
                }}
              >
                <IconRecurringCalendar />
                <span>{t("demand_ui.manage_default_action")}</span>
              </AppButton>
            </AppTooltip>
          </>
        )}
      />

      {loadError ? (
        <AppCallout variant="error" title={t("demand_ui.load_failed")} description={loadError} />
      ) : null}

      {data?.message ? (
        <AppCallout variant="warning" description={data.message} />
      ) : null}

      {!loading && departments.length === 0 ? (
        <AppCallout
          variant="info"
          description={t("demand_ui.setup_required")}
        />
      ) : null}

      {!loading && departments.length > 0 && shiftCount === 0 ? (
        <AppCallout variant="info" description={t("demand_ui.no_shifts")} />
      ) : null}

      {loading ? (
        <div className="demand-v2__week-loading">
          <AppLoadingOverlay open scope="contained" />
        </div>
      ) : (
        <div className="demand-v2__departments">
          {departments.map((department) => (
            <DemandDepartmentCard
              key={department.id}
              department={department}
              dates={dates}
              values={values}
              defaultValues={defaultValues}
              overrides={overrides}
              expanded={expandedDepartmentIds.has(department.id)}
              onToggle={() => toggleDepartment(department.id)}
              onUpdateCell={updateCell}
            />
          ))}
        </div>
      )}

      <DefaultDemandDialog
        open={defaultDialogOpen}
        onClose={() => setDefaultDialogOpen(false)}
        departments={departments}
        dates={dates}
        defaultValues={defaultValues}
        expandedDepartmentIds={defaultExpandedDepartmentIds}
        onToggleDepartment={toggleDefaultDepartment}
        onUpdateDefaultCell={updateDefaultCell}
      />

      {clearDialogOpen ? (
        <AppConfirmDialog
          open
          size="sm"
          title={t("demand_ui.clear_week_title")}
          description={t("demand_ui.clear_week_description", { range: formatWeekRange(dates) })}
          confirmLabel={t("demand_ui.clear_week_confirm")}
          confirmIcon={<IconDelete />}
          confirmTone="error"
          cancelLabel={t("button.cancel")}
          onClose={() => {
            if (clearing) return;
            setClearDialogOpen(false);
          }}
          onConfirm={clearWeek}
        />
      ) : null}
    </AppWorkspace>
  );
};

export default Demand;
